import { Box, Flex, Text } from '../ui';
import type { Names } from '../../types';

type SeatId = keyof Pick<
  Names,
  't1p1Name' | 't1p2Name' | 't2p1Name' | 't2p2Name'
>;

export interface HomeSeat {
  id: SeatId;
  team: 'team1' | 'team2';
  /** The input's accessible name. */
  label: string;
  /** The caps label printed above the field. */
  seatLabel: string;
  placeholder: string;
  /** Where the seat sits on the table (Emerald/Home 1659:21055). */
  place: Record<string, string>;
  /** Where the dealer button sits, against the seat box (whose bottom is the
   *  field's bottom), and whether that is in the label row. */
  dealer: { place: Record<string, string>; inLabelRow: boolean };
}

// Seat geometry, in table px. A seat is a 22pt label, a 4pt gap and a 44pt
// field. The side seats centre 7pt above the table's middle.
const SEAT_HEIGHT = 70;
const FIELD_HEIGHT = 44;

// The dealer button (26pt) follows the board's design 2C (2026-10-26): on the
// felt beside the seat, never over the field. You and Partner: 8pt left of
// the field (the board's 10pt would bring it within 6pt of the stitching at
// 360x800: these fields are wider than the board's cards). You's is level
// with the field's top; Partner's sits 3pt above its bottom, which keeps it
// clear of GAME IN PROGRESS on a Short (360) table. The opponents have GAME
// IN PROGRESS right above them, so theirs takes the end of the label row
// nearest the table centre, 3pt above the field.
const DEALER_BUTTON = 26;
const DEALER_GAP_FIELD = '8px';
const PARTNER_DEALER_LIFT = 3;
const OPPONENT_DEALER_LIFT = 3;
const BESIDE_FIELD = `calc(100% + ${DEALER_GAP_FIELD})`;
const PARTNER_TOP = 20;
const SIDE_CENTRE = 'calc(50% - 7px)';
const YOU_BOTTOM = 44;
/** Halfway between the partner's bottom edge and the opponents' top edge. */
/** The felt print's line height, as a multiple of its font size. */
const TITLE_LINE_HEIGHT = 1.25;
const FELT_PRINT_CENTRE = `calc((${PARTNER_TOP + SEAT_HEIGHT}px + ${SIDE_CENTRE} - ${SEAT_HEIGHT / 2}px) / 2)`;

/** A seat's 22pt label row holds 14pt of text, so the caps start 3.5pt down. */
const LABEL_TEXT_INSET = 3.5;

/**
 * The open felt between the side seats' fields and You's label text. The
 * first-dealer hint sits here, centred: above the side seats there's only room
 * for the print once the table is Short (360), and the hint is too wide to
 * pass between them. It bounds the drawn text, not the label's taller box, or
 * the hint reads a little high.
 */
export const HOME_LOWER_BAND = {
  top: `calc(50% - 7px + ${SEAT_HEIGHT / 2}px)`,
  bottom: `${YOU_BOTTOM + SEAT_HEIGHT - LABEL_TEXT_INSET}px`,
} as const;

// Clockwise from your seat, because play goes clockwise. This is the DOM
// order, so it is also the Tab order and the order of the phone keyboard's
// previous/next arrows: You, Left, Partner, Right.
export const HOME_SEATS: readonly HomeSeat[] = [
  {
    id: 't1p1Name',
    team: 'team1',
    label: 'You',
    seatLabel: 'YOU',
    placeholder: '+ Your name',
    place: {
      bottom: `${YOU_BOTTOM}px`,
      left: '50%',
      transform: 'translateX(-50%)',
    },
    dealer: {
      place: {
        right: BESIDE_FIELD,
        bottom: `${FIELD_HEIGHT - DEALER_BUTTON}px`,
      },
      inLabelRow: false,
    },
  },
  {
    id: 't2p1Name',
    team: 'team2',
    label: 'Left opponent',
    seatLabel: 'OPPONENT',
    placeholder: '+ Add name',
    place: {
      top: SIDE_CENTRE,
      left: 'var(--spades-seat-rim-inset-side)',
      transform: 'translateY(-50%)',
    },
    dealer: {
      place: { right: '0', bottom: `${FIELD_HEIGHT + OPPONENT_DEALER_LIFT}px` },
      inLabelRow: true,
    },
  },
  {
    id: 't1p2Name',
    team: 'team1',
    label: 'Partner',
    seatLabel: 'PARTNER',
    placeholder: '+ Add name',
    place: {
      top: `${PARTNER_TOP}px`,
      left: '50%',
      transform: 'translateX(-50%)',
    },
    dealer: {
      place: { right: BESIDE_FIELD, bottom: `${PARTNER_DEALER_LIFT}px` },
      inLabelRow: false,
    },
  },
  {
    id: 't2p2Name',
    team: 'team2',
    label: 'Right opponent',
    seatLabel: 'OPPONENT',
    placeholder: '+ Add name',
    place: {
      top: SIDE_CENTRE,
      right: 'var(--spades-seat-rim-inset-side)',
      transform: 'translateY(-50%)',
    },
    dealer: {
      place: { left: '0', bottom: `${FIELD_HEIGHT + OPPONENT_DEALER_LIFT}px` },
      inLabelRow: true,
    },
  },
];

export const HOME_SEAT_IDS: readonly string[] = HOME_SEATS.map((s) => s.id);

/** Felt print. It names the state, never the score. */
export function HomeFeltPrint({
  hasGameData,
  round,
}: {
  hasGameData: boolean;
  round: number;
}) {
  return (
    <Flex
      position="absolute"
      // The title line is centred between the partner's field and the
      // opponents' seats (it hung at Figma's 25.23%, right under the partner,
      // and looked high). ROUND n hangs below it, so the title sits at the
      // same height with or without a saved game. The title row is one line
      // (lineHeight 1.25), so half of it is 0.625 × the font size.
      top={FELT_PRINT_CENTRE}
      left="50%"
      transform={`translate(-50%, calc(var(--spades-font-size-sm) * -${TITLE_LINE_HEIGHT / 2}))`}
      direction="column"
      align="center"
      gap="var(--spades-spacing-xs)"
      pointerEvents="none"
      color="var(--spades-mint)"
      fontWeight="700"
      lineHeight={TITLE_LINE_HEIGHT}
      whiteSpace="nowrap"
    >
      <Flex align="center" gap="var(--spades-spacing-sm)" opacity={0.85}>
        <Box width="14px" height="1px" bg="var(--spades-mint)" />
        <Text fontSize="var(--spades-font-size-sm)" letterSpacing="var(--spades-tracking-2xl)">
          {hasGameData ? 'GAME IN PROGRESS' : 'TAKE YOUR SEATS'}
        </Text>
        <Box width="14px" height="1px" bg="var(--spades-mint)" />
      </Flex>
      {hasGameData && (
        <Text
          fontSize="var(--spades-font-size-2xs)"
          letterSpacing="var(--spades-tracking-xl)"
          opacity={0.85}
        >
          {`ROUND ${round}`}
        </Text>
      )}
    </Flex>
  );
}
